import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../config';
import { useNavigate } from 'react-router-dom';
import RecipePriceDetails from '../components/RecipePriceDetails';
import '../styles/mealPlanningModern.css';

const STORAGE_KEY = 'ai-meal-plan-config';

const loadSavedConfig = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const AIMealPlanning = () => {
    const navigate = useNavigate();

    const saved = loadSavedConfig();
    const [numberOfPeople, setNumberOfPeople] = useState(saved?.numberOfPeople ?? 1);
    const [numberOfDays, setNumberOfDays] = useState(saved?.numberOfDays ?? 7);
    const [mealsPerDay, setMealsPerDay] = useState(saved?.mealsPerDay ?? ['lunch', 'dinner']);
    const [minimizeShopping, setMinimizeShopping] = useState(saved?.minimizeShopping ?? true);
    const [optimizeFor, setOptimizeFor] = useState(saved?.optimizeFor ?? '');
    const [generating, setGenerating] = useState(false);
    const [generatedPlan, setGeneratedPlan] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [planName, setPlanName] = useState('');
    const [planDescription, setPlanDescription] = useState('');
    const [hasPreferences, setHasPreferences] = useState(false);
    const [preferenceSummary, setPreferenceSummary] = useState(null);
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    const [cupboardCount, setCupboardCount] = useState(null); // null = not yet loaded
    const [warningModal, setWarningModal] = useState(null); // { title, message, actionLabel, onAction }
    const [batchCost, setBatchCost] = useState(null); // accurate cost with cupboard deductions
    const [loadingBatchCost, setLoadingBatchCost] = useState(false);
    const [consolidatedIngredients, setConsolidatedIngredients] = useState([]);
    const [consolidatedIngredientPrices, setConsolidatedIngredientPrices] = useState({});
    const [accurateCost, setAccurateCost] = useState(null); // reported by RecipePriceDetails

    // Persist config whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ numberOfPeople, numberOfDays, mealsPerDay, minimizeShopping, optimizeFor }));
        } catch {}
    }, [numberOfPeople, numberOfDays, mealsPerDay, minimizeShopping, optimizeFor]);

    useEffect(() => {
        checkUserPreferences();
    }, []);

    const checkUserPreferences = async () => {
        try {
            const userId = window.localStorage.getItem('userID');
            const [prefsResponse, cupboardResponse] = await Promise.allSettled([
                axiosInstance.get('/meal-planning/preferences'),
                userId ? axiosInstance.get(`/cupboard/${userId}`) : Promise.resolve(null)
            ]);

            if (prefsResponse.status === 'fulfilled' && prefsResponse.value?.data?.success) {
                const prefs = prefsResponse.value.data.preferences;
                const hasMeaningfulData = prefs._id ||
                    prefs.budgetConstraint ||
                    (prefs.allergies && prefs.allergies.length > 0) ||
                    (prefs.favoriteCuisines && prefs.favoriteCuisines.length > 0) ||
                    Object.values(prefs.dietaryRestrictions || {}).some(Boolean);
                setHasPreferences(!!hasMeaningfulData);
                setPreferenceSummary({
                    dietary: Object.entries(prefs.dietaryRestrictions || {}).filter(([_, v]) => v).map(([k]) => k),
                    allergies: prefs.allergies || [],
                    dislikedFoods: prefs.dislikedFoods || [],
                    cuisines: prefs.favoriteCuisines || [],
                    budget: prefs.budgetConstraint || null,
                    minimizeWaste: prefs.minimizeWaste
                });
            }

            if (cupboardResponse.status === 'fulfilled' && cupboardResponse.value?.data?.success) {
                setCupboardCount(cupboardResponse.value.data.items?.length ?? 0);
            } else {
                setCupboardCount(0);
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
        } finally {
            setPreferencesLoaded(true);
        }
    };

    const toggleMealType = (mealType) => {
        if (mealsPerDay.includes(mealType)) setMealsPerDay(mealsPerDay.filter(m => m !== mealType));
        else setMealsPerDay([...mealsPerDay, mealType]);
    };

    const handleMinimizeShoppingToggle = () => {
        const turningOn = !minimizeShopping;
        if (turningOn && cupboardCount === 0) {
            setWarningModal({
                title: 'No cupboard ingredients',
                message: 'Minimize shopping list works best when you have ingredients in your cupboard. The AI picks recipes that share ingredients you already own, reducing what you need to buy. Add ingredients to your cupboard first for the best results.',
                actionLabel: 'Go to cupboard',
                onAction: () => navigate('/cupboard')
            });
            return;
        }
        setMinimizeShopping(turningOn);
    };

    const handleOptimizeForChange = (value) => {
        if (value === 'low_cost' && !preferenceSummary?.budget) {
            setWarningModal({
                title: 'No daily budget set',
                message: 'Optimizing for low cost works best with a daily budget. Without one, the AI has no target to aim for. Set a daily budget in your preferences so the AI can keep meals within your spending limit.',
                actionLabel: 'Set budget in preferences',
                onAction: () => navigate('/meal-planning/preferences')
            });
            return;
        }
        setOptimizeFor(value);
    };

    // Consolidate ingredients across all recipes (scale by servings, merge duplicates)
    // This mirrors the server-side consolidateIngredients() in mealPlanningService.js
    const consolidateRecipeIngredients = (recipes) => {
        const map = new Map();
        for (const recipe of recipes) {
            if (!recipe.ingredients?.length) continue;
            const multiplier = recipe.originalServings > 0
                ? recipe.servings / recipe.originalServings
                : 1;
            for (const ing of recipe.ingredients) {
                const key = (ing.name || '').toLowerCase().trim();
                const amount = (parseFloat(ing.amount) || 0) * multiplier;
                const unit = (ing.unit || '').toLowerCase().trim();
                if (map.has(key)) {
                    const existing = map.get(key);
                    const unitEntry = existing.amounts.find(a => a.unit === unit);
                    if (unitEntry) {
                        unitEntry.amount += amount;
                    } else {
                        existing.amounts.push({ amount, unit: ing.unit || '' });
                    }
                } else {
                    map.set(key, {
                        name: ing.name,
                        amounts: [{ amount, unit: ing.unit || '' }]
                    });
                }
            }
        }
        return Array.from(map.values()).map(ing => ({
            name: ing.name,
            amount: ing.amounts[0]?.amount || 0,
            unit: ing.amounts[0]?.unit || '',
            amounts: ing.amounts
        }));
    };

    // Calculate cost using consolidated ingredients + cupboard deductions —
    // exactly the same as MealPlanDetails.js: fetch ingredientPrices from the API,
    // then let RecipePriceDetails (rendered hidden below) compute the true total via onTotalCostChange.
    const calculateAccurateCost = async (recipes) => {
        if (!recipes?.length) return;
        setLoadingBatchCost(true);
        setBatchCost(null);
        setAccurateCost(null);
        setConsolidatedIngredients([]);
        setConsolidatedIngredientPrices({});
        try {
            const userId = window.localStorage.getItem('userID');
            const consolidated = consolidateRecipeIngredients(recipes);

            // Flatten multi-unit amounts the same way MealPlanDetails does
            const ingredientsForCalc = consolidated.flatMap(ing => {
                if (ing.amounts && ing.amounts.length > 1) {
                    return ing.amounts.map(amt => ({
                        name: ing.name,
                        amount: amt.amount,
                        unit: amt.unit
                    }));
                }
                return [{ name: ing.name, amount: ing.amount, unit: ing.unit }];
            });

            const response = await axiosInstance.post('/meal-planning/calculate-consolidated-cost', {
                ingredients: ingredientsForCalc,
                userId
            });

            if (response.data.success) {
                const skipped = response.data.cupboardSavings?.ingredientsSkipped || [];
                // Store the ingredient prices and consolidated list so RecipePriceDetails
                // can compute the accurate total (same as MealPlanDetails does).
                setConsolidatedIngredients(consolidated);
                setConsolidatedIngredientPrices(response.data.ingredientPrices || {});
                setBatchCost({
                    cupboardSavings: skipped.length,
                    cupboardItemsSkipped: skipped
                });
                // loadingBatchCost stays true until RecipePriceDetails fires onTotalCostChange
            }
        } catch (err) {
            console.error('Error calculating consolidated cost:', err);
            setLoadingBatchCost(false);
        }
    };

    const generateMealPlan = async () => {
        if (mealsPerDay.length === 0) {
            alert('Please select at least one meal type');
            return;
        }
        setGenerating(true);
        setError(null);
        setGeneratedPlan(null);
        setBatchCost(null);
        setAccurateCost(null);
        setConsolidatedIngredients([]);
        setConsolidatedIngredientPrices({});
        try {
            const response = await axiosInstance.post('/meal-planning/ai-generate', {
                numberOfPeople: parseInt(numberOfPeople),
                numberOfDays: parseInt(numberOfDays),
                mealsPerDay,
                minimizeShopping,
                optimizeFor: optimizeFor || undefined
            });
            if (response.data.success) {
                const plan = response.data.mealPlan;
                setGeneratedPlan(plan);
                setPlanName(`AI Meal Plan - ${new Date().toLocaleDateString()}`);
                setPlanDescription(`${numberOfDays}-day plan for ${numberOfPeople} ${numberOfPeople === 1 ? 'person' : 'people'}`);
                // Calculate accurate cost with cupboard deductions (same as MealPlanDetails)
                calculateAccurateCost(plan.recipes);
            }
        } catch (err) {
            console.error('Error generating meal plan:', err);
            setError(err.response?.data?.details || err.response?.data?.error || 'Failed to generate meal plan. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    const saveMealPlan = async () => {
        if (!planName.trim()) {
            alert('Please enter a meal plan name');
            return;
        }
        if (!generatedPlan?.recipes?.length) {
            alert('No meal plan to save');
            return;
        }
        setSaving(true);
        try {
            const startDate = new Date();
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + numberOfDays);
            const response = await axiosInstance.post('/meal-planning/plans', {
                name: planName,
                description: planDescription,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                recipes: generatedPlan.recipes.map(r => ({ recipeId: r.recipeId, servings: r.servings, mealType: r.mealType }))
            });
            if (response.data.success) {
                alert('Meal plan saved successfully!');
                navigate(`/meal-plan/${response.data.mealPlan._id}`);
            }
        } catch (err) {
            console.error('Error saving meal plan:', err);
            alert('Failed to save meal plan');
        } finally {
            setSaving(false);
        }
    };

    const groupRecipesByDay = () => {
        if (!generatedPlan?.mealsByDay) return {};
        return generatedPlan.mealsByDay;
    };

    return (
        <>
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                {/* Header - Mob style */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">AI Meal Planning</h1>
                        <p className="text-sm text-gray-600 mt-0.5">Let AI create your meal plan</p>
                    </div>
                    <button
                        onClick={() => navigate('/meal-planning')}
                        className="px-3 py-2 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors shrink-0"
                    >
                        ← Back to Meal Planning
                    </button>
                </div>

                <div className="space-y-4 sm:space-y-5">
                    {/* Preferences banner — always shown once loaded */}
                    {preferencesLoaded && (
                        hasPreferences && preferenceSummary ? (
                            <div className="rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200 p-3 sm:p-4">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                    <div>
                                        <h3 className="text-sm font-semibold text-amber-900">Your preferences are active</h3>
                                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-amber-800">
                                            {preferenceSummary.budget && <span>Daily budget: R{preferenceSummary.budget}</span>}
                                            {preferenceSummary.dietary?.length > 0 && <span>Dietary: {preferenceSummary.dietary.join(', ')}</span>}
                                            {preferenceSummary.allergies?.length > 0 && <span>Allergies: {preferenceSummary.allergies.join(', ')}</span>}
                                            {preferenceSummary.cuisines?.length > 0 && <span>Cuisines: {preferenceSummary.cuisines.join(', ')}</span>}
                                            {preferenceSummary.minimizeWaste && <span>Minimize waste</span>}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => navigate('/meal-planning/preferences')}
                                        className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-200 text-amber-900 hover:bg-amber-300 transition-colors"
                                    >
                                        Edit preferences
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-3 sm:p-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-amber-900">Set your preferences</h3>
                                        <p className="text-xs text-amber-800 mt-0.5">Add dietary needs, allergies, daily budget and favourite cuisines for smarter meal plans.</p>
                                    </div>
                                    <button
                                        onClick={() => navigate('/meal-planning/preferences')}
                                        className="shrink-0 px-4 py-2 rounded-full text-sm font-semibold bg-amber-200 text-amber-900 hover:bg-amber-300 transition-colors"
                                    >
                                        Set preferences
                                    </button>
                                </div>
                            </div>
                        )
                    )}

                    {/* Config form - compact */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Configure your plan</h2>
                            <p className="text-xs text-gray-500 mt-0.5">People, days and meal types</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">People</label>
                                    <select
                                        value={numberOfPeople}
                                        onChange={(e) => setNumberOfPeople(Number(e.target.value))}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 bg-white appearance-none"
                                    >
                                        {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                                            <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Days</label>
                                    <select
                                        value={numberOfDays}
                                        onChange={(e) => setNumberOfDays(Number(e.target.value))}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 bg-white appearance-none"
                                    >
                                        {Array.from({ length: 14 }, (_, i) => i + 1).map(n => (
                                            <option key={n} value={n}>{n} {n === 1 ? 'day' : 'days'}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">Meals to include</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {['lunch', 'dinner'].map(mealType => (
                                        <button
                                            key={mealType}
                                            type="button"
                                            onClick={() => toggleMealType(mealType)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${mealsPerDay.includes(mealType) ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">Optimize for</label>
                                <select
                                    value={optimizeFor}
                                    onChange={(e) => handleOptimizeForChange(e.target.value)}
                                    className="w-auto min-w-[160px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 bg-white appearance-none"
                                >
                                    <option value="">None</option>
                                    <option value="low_cost">Low cost</option>
                                    <option value="low_calorie">Low calorie</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Prioritize cheaper or lighter recipes when available</p>
                            </div>
                            {/* Custom checkbox - black themed */}
                            <button
                                type="button"
                                onClick={handleMinimizeShoppingToggle}
                                className="flex items-start gap-3 w-full text-left p-2 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                            >
                                <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${minimizeShopping ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-400'}`}>
                                    {minimizeShopping && (
                                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </span>
                                <div>
                                    <span className="text-sm font-medium text-gray-900">Minimize shopping list</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Pick recipes that share ingredients</p>
                                </div>
                            </button>
                            <p className="text-xs text-gray-500">
                                AI will generate <strong>{numberOfDays * mealsPerDay.length}</strong> meals ({mealsPerDay.length}/day × {numberOfDays} days) for <strong>{numberOfPeople}</strong> {numberOfPeople === 1 ? 'person' : 'people'}.
                            </p>
                            <button
                                onClick={generateMealPlan}
                                disabled={generating || mealsPerDay.length === 0}
                                className="w-full py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                            >
                                {generating ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    'Generate AI meal plan'
                                )}
                            </button>
                            {error && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                                    <strong>Error:</strong> {error}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Generated plan */}
                    {generatedPlan && (
                        <>
                            {/* Plan summary — single cost card */}
                            {(() => {
                                const totalBudget = preferenceSummary?.budget ? preferenceSummary.budget * numberOfDays : null;
                                const netCost = accurateCost ?? null;
                                const overBudget = totalBudget && netCost != null && netCost > totalBudget;
                                const withinBudget = totalBudget && netCost != null && !overBudget;
                                const hasCupboardSavings = (batchCost?.cupboardSavings ?? 0) > 0;
                                return (
                                    <div className={`rounded-xl border p-4 ${overBudget ? 'bg-red-50 border-red-200' : hasCupboardSavings ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                                                    {hasCupboardSavings ? 'Estimated total cost (cupboard applied)' : 'Estimated total cost'}
                                                </p>
                                                {loadingBatchCost ? (
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                                        <span className="text-sm text-gray-500">Calculating…</span>
                                                    </div>
                                                ) : netCost != null ? (
                                                    <p className={`text-2xl font-bold ${overBudget ? 'text-red-700' : 'text-gray-900'}`}>
                                                        R{netCost.toFixed(2)}
                                                    </p>
                                                ) : (
                                                    <p className="text-2xl font-bold text-gray-400">—</p>
                                                )}
                                                {hasCupboardSavings && !loadingBatchCost && (
                                                    <p className="text-xs text-amber-700 mt-0.5">
                                                        {batchCost.cupboardSavings} ingredient{batchCost.cupboardSavings !== 1 ? 's' : ''} covered by your cupboard
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-3 text-center">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{generatedPlan.totalMeals}</p>
                                                    <p className="text-xs text-gray-500">Meals</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{generatedPlan.summary?.totalServings || 0}</p>
                                                    <p className="text-xs text-gray-500">Servings</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{generatedPlan.summary?.averageCookingTime || 0} min</p>
                                                    <p className="text-xs text-gray-500">Avg. cook time</p>
                                                </div>
                                            </div>
                                        </div>
                                        {totalBudget && netCost != null && !loadingBatchCost && (
                                            <div className={`mt-2 pt-2 border-t text-sm font-medium ${overBudget ? 'border-red-200 text-red-700' : 'border-amber-200 text-amber-800'}`}>
                                                {overBudget
                                                    ? `Over budget by R${(netCost - totalBudget).toFixed(2)} (budget: R${totalBudget.toFixed(2)})`
                                                    : `✓ Within budget — R${(totalBudget - netCost).toFixed(2)} remaining of R${totalBudget.toFixed(2)}`
                                                }
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Meals by day - compact cards */}
                            {Object.entries(groupRecipesByDay()).map(([day, dayRecipes]) => (
                                <div key={day} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="p-3 sm:p-4 border-b border-gray-100">
                                        <h2 className="text-sm font-semibold text-gray-900">Day {day}</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">{dayRecipes.length} meals</p>
                                    </div>
                                    <div className="p-3 sm:p-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                                            {dayRecipes.map((recipe, index) => (
                                                <div key={index} className="rounded-lg border border-gray-100 overflow-hidden bg-gray-50 group hover:border-gray-200 transition-colors">
                                                    <div className="aspect-[4/3] overflow-hidden bg-gray-200 cursor-pointer" onClick={() => navigate(`/recipe/${recipe.recipeId}`)}>
                                                        <img src={recipe.recipeImage} alt={recipe.recipeName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                                    </div>
                                                    <div className="p-2 sm:p-2.5">
                                                        <span className="text-xs font-medium text-amber-700">{recipe.mealType}</span>
                                                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 leading-tight mt-0.5 cursor-pointer" onClick={() => navigate(`/recipe/${recipe.recipeId}`)}>{recipe.recipeName}</h3>
                                                        <p className="text-xs text-gray-500 mt-0.5">{recipe.servings} servings · {recipe.cookingTime} min</p>
                                                        {recipe.pricePerServing > 0 && <p className="text-xs text-green-600 mt-0.5">R{recipe.pricePerServing.toFixed(2)}/serving</p>}
                                                        <button onClick={() => navigate(`/recipe/${recipe.recipeId}`)} className="mt-2 w-full py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800">View recipe</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Save section - compact */}
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="p-3 sm:p-4 border-b border-gray-100">
                                    <h2 className="text-sm font-semibold text-gray-900">Save your plan</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">Name and optional description</p>
                                </div>
                                <div className="p-3 sm:p-4 space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Plan name *</label>
                                        <input type="text" value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="e.g. Weekly Meal Plan" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                        <textarea value={planDescription} onChange={(e) => setPlanDescription(e.target.value)} placeholder="Optional" rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 resize-none" />
                                    </div>
                                    <button onClick={saveMealPlan} disabled={saving} className="w-full py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
                                        {saving ? 'Saving...' : 'Save meal plan'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>

        {/* Hidden RecipePriceDetails — renders off-screen to compute the accurate cost
            using the same brand/special/pack-size logic as MealPlanDetails.js */}
        {consolidatedIngredients.length > 0 && Object.keys(consolidatedIngredientPrices).length > 0 && (
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
                <RecipePriceDetails
                    ingredients={consolidatedIngredients}
                    ingredientPrices={consolidatedIngredientPrices}
                    onTotalCostChange={(cost) => {
                        setAccurateCost(cost);
                        setLoadingBatchCost(false);
                    }}
                    onRequireSignIn={() => {}}
                />
            </div>
        )}

        {/* Warning modal */}
        {warningModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                            <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">{warningModal.title}</h3>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">{warningModal.message}</p>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={() => setWarningModal(null)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                            Dismiss
                        </button>
                        <button
                            onClick={() => { setWarningModal(null); warningModal.onAction(); }}
                            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 transition-colors"
                        >
                            {warningModal.actionLabel}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default AIMealPlanning;
